import { ReactElement, useEffect, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { Box, DraggablePopup } from '@ui/structure'
import { Tapeline } from '@editorStores'
import TapelineSettings, { type TapelineAttributesPatch } from './TapelineSettings'

interface Props {
  selectedId: string | null
  selectedLine?: Tapeline
  onUpdateSelectedLine: (patch: TapelineAttributesPatch) => void
  onDeleteSelectedLine: () => void
  onClose: () => void
}

const TapelineSettingsPopup = ({
  selectedId,
  selectedLine,
  onUpdateSelectedLine,
  onDeleteSelectedLine,
  onClose,
}: Props): ReactElement => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [popupInstanceKey, setPopupInstanceKey] = useState(0)
  const [anchorPosition, setAnchorPosition] = useState({ x: 0, y: 0 })
  const lastPointerPositionRef = useRef({ x: 0, y: 0 })
  const popoverAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const setLastPointerPosition = (event: PointerEvent): void => {
      lastPointerPositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      }
    }

    window.addEventListener('pointerdown', setLastPointerPosition, true)

    return () => {
      window.removeEventListener('pointerdown', setLastPointerPosition, true)
    }
  }, [])

  useEffect(() => {
    if (selectedId) {
      setAnchorPosition(lastPointerPositionRef.current)
      setIsPopoverOpen(true)
      setPopupInstanceKey(previous => previous + 1)
      return
    }
    setIsPopoverOpen(false)
  }, [selectedId])

  const closeTapelinePopover = (): void => {
    setIsPopoverOpen(false)
    onClose()
  }

  return (
    <>
      <Box
        ref={popoverAnchorRef}
        sx={{
          position: 'fixed',
          top: anchorPosition.y,
          left: anchorPosition.x,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
      <DraggablePopup
        key={popupInstanceKey}
        title={<Typography>Hilfslinie Einstellungen</Typography>}
        closePopover={closeTapelinePopover}
        anchorEl={isPopoverOpen ? popoverAnchorRef.current || undefined : undefined}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        paperSx={{ maxWidth: 380 }}
        childrenBoxSx={{ overflow: 'auto', maxWidth: 380 }}
      >
        <TapelineSettings
          selectedLine={selectedLine}
          onUpdateSelectedLine={onUpdateSelectedLine}
          onDeleteSelectedLine={onDeleteSelectedLine}
        />
      </DraggablePopup>
    </>
  )
}

export default TapelineSettingsPopup
